import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useFeatures } from '@/lib/useFeatures';
import { Play, Pause, SkipForward, Volume2, VolumeX, Radio as RadioIcon, Mic, Wifi } from 'lucide-react';

/**
 * PIRATE RADIO — /radio
 *
 *  • Plays the admin-curated track list on loop.
 *  • Between every Nth track, an AI DJ commentary line is generated server-side
 *    and spoken locally via the Web Speech API (zero external dep).
 *  • Heavy on atmosphere: glowing "ON AIR" sign, animated waveform, scrolling subtitles.
 */

function speakDJLine(text, onEnd) {
  return new Promise((resolve) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      const voices = window.speechSynthesis.getVoices();
      // Prefer a deep masculine voice if available.
      const preferred = voices.find((v) => /Daniel|Alex|Aaron|Google US English|US English Male/i.test(v.name))
        || voices.find((v) => /en-US|en_GB|en-GB/i.test(v.lang));
      if (preferred) u.voice = preferred;
      u.rate = 0.95;
      u.pitch = 0.85;
      u.volume = 0.9;
      u.onend = () => { onEnd?.(); resolve(); };
      u.onerror = () => { onEnd?.(); resolve(); };
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch (e) {
      onEnd?.();
      resolve();
    }
  });
}

export default function Radio() {
  const { isOn, loading: flagsLoading } = useFeatures();
  const [config, setConfig] = useState(null);
  const [trackIdx, setTrackIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [djLine, setDjLine] = useState('');
  const [djSpeaking, setDjSpeaking] = useState(false);
  const [tracksPlayed, setTracksPlayed] = useState(0);
  const audioRef = useRef(null);
  const trackOrderRef = useRef([]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/radio/config');
      setConfig(data);
      // Build (possibly shuffled) play order.
      const order = (data.tracks || []).map((t, i) => i);
      if (data.shuffle) order.sort(() => Math.random() - 0.5);
      trackOrderRef.current = order;
    } catch (e) {
      setConfig({ tracks: [] });
    }
  }, []);
  useEffect(() => { if (isOn('pirate_radio')) loadConfig(); }, [isOn, loadConfig]);

  // Stop everything on unmount.
  useEffect(() => () => {
    try { audioRef.current?.pause(); } catch {}
    try { window.speechSynthesis.cancel(); } catch {}
  }, []);

  if (flagsLoading) return <div className="kc-container py-16 text-muted-kc" data-testid="radio-loading">Tuning…</div>;
  if (!isOn('pirate_radio')) {
    return (
      <div className="kc-container py-16" data-testid="radio-disabled">
        <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Pirate Radio</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Off air.</h1>
        <p className="mt-3 text-muted-kc max-w-prose">The station is silent right now. Check back soon.</p>
      </div>
    );
  }

  const tracks = config?.tracks || [];
  const order = trackOrderRef.current.length === tracks.length ? trackOrderRef.current : tracks.map((_, i) => i);
  const currentTrack = tracks[order[trackIdx]] || null;

  const advance = async () => {
    const nextIdx = (trackIdx + 1) % Math.max(1, tracks.length);
    const tracksSincePatter = tracksPlayed + 1;
    const djEvery = Math.max(1, parseInt(config?.dj_frequency || 2));
    const nextTrack = tracks[order[nextIdx]] || null;

    if (tracksSincePatter % djEvery === 0 && tracks.length > 1) {
      // DJ line first, then advance.
      try {
        setDjSpeaking(true);
        const { data } = await api.post('/radio/dj-line', {
          previous_track: currentTrack ? { title: currentTrack.title, mood: currentTrack.mood } : null,
          next_track: nextTrack ? { title: nextTrack.title, mood: nextTrack.mood } : null,
          listener_count: 1,
        });
        const line = data.text || 'Rolling on.';
        setDjLine(line);
        await speakDJLine(line);
      } catch {
        // continue without commentary
      } finally {
        setDjSpeaking(false);
      }
    }
    setTracksPlayed((n) => n + 1);
    setTrackIdx(nextIdx);
    // Auto-play next track.
    setTimeout(() => {
      try { audioRef.current?.play(); } catch {}
    }, 250);
  };

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (!playing) {
      try { await a.play(); } catch {}
    } else {
      a.pause();
      try { window.speechSynthesis.cancel(); } catch {}
    }
  };

  const toggleMute = () => {
    setMuted((m) => {
      const nm = !m;
      if (audioRef.current) audioRef.current.muted = nm;
      return nm;
    });
  };

  return (
    <div className="bg-ink text-white min-h-[calc(100vh-5rem)] relative overflow-hidden" data-testid="radio-page">
      {/* Atmospheric background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(212,164,55,0.15),transparent_55%),radial-gradient(circle_at_70%_75%,rgba(50,40,80,0.2),transparent_55%)]" aria-hidden/>
      <div className="absolute inset-0 cinematic-grain opacity-[0.04] mix-blend-overlay pointer-events-none" aria-hidden/>

      <div className="relative kc-container py-12 md:py-20">
        {/* On Air sign */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`relative w-3.5 h-3.5 rounded-full ${playing ? 'bg-red-500' : 'bg-white/20'}`}>
              {playing && <span className="absolute inset-0 rounded-full bg-red-500 animate-ping"/>}
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">{playing ? 'On Air' : 'Off Air'}</div>
            <span className="text-white/25">·</span>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/60 inline-flex items-center gap-1"><Wifi size={11}/> {tracks.length} track{tracks.length === 1 ? '' : 's'}</div>
          </div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">{config?.station_name || 'Kickass Pirate Radio'}</div>
        </div>

        {/* Hero */}
        <div className="mt-10 grid lg:grid-cols-[1fr_1fr] gap-10 items-center">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Pirate Radio</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.95] mt-3">{(config?.tagline || 'Tools that train the mind — on air.').split('—')[0]}<br/><span className="text-[color:var(--kc-gold)]">{(config?.tagline || '').split('—')[1] ? '— ' + (config?.tagline || '').split('—')[1].trim() : 'on air.'}</span></h1>
            <p className="mt-5 text-white/65 text-base md:text-lg max-w-prose">
              Mickey's soundtrack on loop. AI DJ between tracks. Hit play and let it run while you work.
              No ads. No talking heads. Just signal.
            </p>

            {/* Now Playing */}
            <div className="mt-8 bg-white/[0.05] border border-white/10 rounded-[var(--kc-radius-md)] p-5" data-testid="radio-now-playing">
              <div className="text-[10px] tracking-[0.18em] uppercase text-white/45">{djSpeaking ? 'On Mic' : 'Now playing'}</div>
              {djSpeaking ? (
                <div className="mt-2 inline-flex items-center gap-2 font-display uppercase text-xl md:text-2xl text-[color:var(--kc-gold)]" data-testid="radio-dj-line">
                  <Mic size={16}/> {djLine || 'Mick is on the line…'}
                </div>
              ) : currentTrack ? (
                <div className="mt-2">
                  <div className="font-display uppercase text-xl md:text-2xl text-white truncate">{currentTrack.title}</div>
                  {currentTrack.mood && <div className="text-xs uppercase tracking-wider text-white/55 mt-0.5">{currentTrack.mood}</div>}
                </div>
              ) : (
                <div className="mt-2 text-white/55 text-sm">No tracks loaded yet. The admin can add tracks to start the station.</div>
              )}

              {/* Waveform */}
              <div className="mt-4 h-12 flex items-end gap-[3px]" aria-hidden>
                {[...Array(40)].map((_, i) => (
                  <span key={i} className="flex-1 rounded-t" style={{
                    height: `${playing ? (Math.abs(Math.sin((Date.now()/200) + i/2)) * 80 + 12) : 4}%`,
                    background: 'linear-gradient(to top, rgba(212,164,55,0.15), rgba(212,164,55,0.75))',
                    transition: 'height 0.35s ease-out',
                    animation: playing ? `radio-wave-${i % 4} 1.${i % 7 + 1}s ease-in-out infinite alternate` : undefined,
                  }}/>
                ))}
              </div>

              {/* Controls */}
              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={togglePlay}
                  disabled={tracks.length === 0}
                  className="h-12 w-12 inline-flex items-center justify-center rounded-full bg-[color:var(--kc-gold)] text-black hover:bg-[color:var(--kc-gold-2)] transition-colors disabled:opacity-30"
                  data-testid="radio-play"
                  aria-label={playing ? 'Pause' : 'Play'}
                >
                  {playing ? <Pause size={18}/> : <Play size={18}/>}
                </button>
                <button
                  type="button"
                  onClick={advance}
                  disabled={tracks.length === 0}
                  className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-30"
                  aria-label="Skip"
                  data-testid="radio-skip"
                >
                  <SkipForward size={16}/>
                </button>
                <button
                  type="button"
                  onClick={toggleMute}
                  className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                  aria-label={muted ? 'Unmute' : 'Mute'}
                  data-testid="radio-mute"
                >
                  {muted ? <VolumeX size={16}/> : <Volume2 size={16}/>}
                </button>
                <div className="ml-2 text-[10px] tracking-[0.18em] uppercase text-white/40">{tracksPlayed} played</div>
              </div>

              <audio
                ref={audioRef}
                src={currentTrack?.url || ''}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={advance}
                preload="auto"
                data-testid="radio-audio"
              />
            </div>
          </div>

          {/* Vinyl */}
          <div className="flex items-center justify-center" data-testid="radio-vinyl-wrap">
            <div className={`relative w-72 h-72 md:w-96 md:h-96 rounded-full bg-gradient-to-br from-black to-zinc-900 ${playing ? 'animate-spin-slow' : ''}`}>
              <div className="absolute inset-2 rounded-full border border-white/10"/>
              <div className="absolute inset-8 rounded-full border border-white/5"/>
              <div className="absolute inset-16 rounded-full border border-white/5"/>
              <div className="absolute inset-24 rounded-full border border-white/5"/>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-gradient-to-br from-[color:var(--kc-gold)] to-[color:var(--kc-gold-2)] flex items-center justify-center text-black">
                  <RadioIcon size={32}/>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Playlist */}
        {tracks.length > 0 && (
          <div className="mt-14" data-testid="radio-playlist">
            <div className="text-[10px] tracking-[0.18em] uppercase text-white/45 mb-3">Tonight's set</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
              {order.map((origIdx, slot) => {
                const t = tracks[origIdx];
                if (!t) return null;
                const active = slot === trackIdx;
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => { setTrackIdx(slot); setTimeout(() => audioRef.current?.play().catch(() => {}), 150); }}
                    className={`text-left p-3 rounded-[var(--kc-radius-sm)] border transition-all ${active ? 'border-[color:var(--kc-gold)] bg-[color:var(--kc-gold)]/10' : 'border-white/10 hover:border-white/30 hover:bg-white/5'}`}
                    data-testid={`radio-track-${t.id}`}
                  >
                    <div className="text-[9px] tracking-[0.18em] uppercase text-white/40">{String(slot + 1).padStart(2, '0')}</div>
                    <div className={`font-display uppercase truncate ${active ? 'text-[color:var(--kc-gold)]' : 'text-white'}`}>{t.title}</div>
                    {t.mood && <div className="text-[10px] text-white/45 uppercase tracking-wider truncate">{t.mood}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .cinematic-grain { background-image: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.15) 0px, transparent 1px); background-size: 3px 3px; }
        .animate-spin-slow { animation: spin 24s linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @keyframes radio-wave-0 { from { transform: scaleY(0.5); } to { transform: scaleY(1.1); } }
        @keyframes radio-wave-1 { from { transform: scaleY(0.6); } to { transform: scaleY(1.0); } }
        @keyframes radio-wave-2 { from { transform: scaleY(0.4); } to { transform: scaleY(0.95); } }
        @keyframes radio-wave-3 { from { transform: scaleY(0.7); } to { transform: scaleY(1.05); } }
      `}</style>
    </div>
  );
}
